import { useCallback, useEffect, useRef, useState } from 'react';
import { AskError, askQuestion, fetchSession } from '../lib/askApi';

/**
 * The whole voice round trip: record, upload, play the answer.
 *
 * Most of what is here exists because of Safari on iOS, which is strict about
 * when audio may start and which container formats it will record. The
 * comments mark those places specifically, because none of it is guessable
 * from the code alone.
 */

// A fraction of a second of silence, used to unlock audio playback inside the
// tap that starts a recording.
const SILENT_MP3 =
  'data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tAwAAAAAAAAAAAAAAAAAAAAAAASW5mbwAAAA8AAAAEAAABIADAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMD/////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAASDs90hvAAAAAAAAAAAAAAAAAAAA//sQxAADwAABpAAAACAAADSAAAAETEFNRTMuMTAwVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV//sQxDAAAAABpAAAACAAADSAAAAEVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV';

const MAX_RECORDING_MS = 25000;

// Safari records mp4 and has never supported webm. Chrome and Firefox prefer
// opus in webm. Probing in this order gets the best available on each.
const PREFERRED_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
];

function pickMimeType() {
  if (typeof MediaRecorder === 'undefined') return '';
  for (const type of PREFERRED_TYPES) {
    if (MediaRecorder.isTypeSupported?.(type)) return type;
  }
  return '';
}

export function micSupported() {
  return Boolean(
    typeof navigator !== 'undefined' &&
      navigator.mediaDevices?.getUserMedia &&
      typeof MediaRecorder !== 'undefined',
  );
}

export const STATUS = {
  idle: 'idle',
  listening: 'listening',
  thinking: 'thinking',
  speaking: 'speaking',
};

export function useVoiceAsk({ language }) {
  const [status, setStatus] = useState(STATUS.idle);
  const [level, setLevel] = useState(0);
  const [error, setError] = useState(null);
  const [answer, setAnswer] = useState(null);

  const audioElementRef = useRef(null);
  const unlockedRef = useRef(false);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const analyserRef = useRef(null);
  const audioContextRef = useRef(null);
  const rafRef = useRef(null);
  const stopTimerRef = useRef(null);
  const abortRef = useRef(null);
  const objectUrlRef = useRef(null);
  const languageRef = useRef(language);

  useEffect(() => {
    languageRef.current = language;
  }, [language]);

  // One audio element for the life of the page. Creating a new one per answer
  // does not work on iOS: only an element that was played during a user
  // gesture may play later, and the answer arrives seconds after the tap.
  useEffect(() => {
    const element = new Audio();
    element.preload = 'auto';
    element.setAttribute('playsinline', '');
    audioElementRef.current = element;

    return () => {
      element.pause();
      element.src = '';
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  const stopMeter = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    analyserRef.current = null;
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    setLevel(0);
  }, []);

  // Releasing the microphone the instant recording ends matters for more than
  // tidiness. While a recording session is open, iOS routes playback to the
  // earpiece at low volume, so the answer would come back almost inaudible.
  const releaseMicrophone = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    stopMeter();
  }, [stopMeter]);

  const cleanup = useCallback(() => {
    if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
    stopTimerRef.current = null;
    releaseMicrophone();
    recorderRef.current = null;
    chunksRef.current = [];
  }, [releaseMicrophone]);

  useEffect(() => cleanup, [cleanup]);

  const playAnswer = useCallback(async (blob) => {
    const element = audioElementRef.current;
    if (!element) return false;

    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    objectUrlRef.current = URL.createObjectURL(blob);
    element.src = objectUrlRef.current;

    try {
      await element.play();
      return true;
    } catch {
      return false;
    }
  }, []);

  const runMeter = useCallback((stream) => {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;

    const context = new AudioContextClass();
    audioContextRef.current = context;
    const source = context.createMediaStreamSource(stream);
    const analyser = context.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);
    analyserRef.current = analyser;

    const data = new Uint8Array(analyser.frequencyBinCount);
    const tick = () => {
      if (!analyserRef.current) return;
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i += 1) {
        const deviation = (data[i] - 128) / 128;
        sum += deviation * deviation;
      }
      setLevel(Math.min(1, Math.sqrt(sum / data.length) * 3.2));
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();
  }, []);

  const send = useCallback(
    async ({ audio, text }) => {
      setStatus(STATUS.thinking);
      setError(null);

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const session = await fetchSession(controller.signal);
        const result = await askQuestion({
          token: session.token,
          audio,
          text,
          language: languageRef.current,
          signal: controller.signal,
        });

        setAnswer(result);

        if (result.audio) {
          setStatus(STATUS.speaking);
          const played = await playAnswer(result.audio);
          if (!played) {
            // Playback refused, which on iPhone usually means the ringer
            // switch is off. The text is all that is left to offer.
            setStatus(STATUS.idle);
            setError({ kind: 'playback', message: 'Could not play audio.' });
          }
        } else {
          setStatus(STATUS.idle);
          setError({ kind: 'noAudio', message: 'Audio is unavailable.' });
        }
      } catch (caught) {
        if (caught.name === 'AbortError') {
          setStatus(STATUS.idle);
          return;
        }
        const problem =
          caught instanceof AskError
            ? { kind: caught.kind, message: caught.message, retryAfter: caught.retryAfter }
            : { kind: 'network', message: 'Could not reach the server.' };
        setError(problem);
        setStatus(STATUS.idle);
      } finally {
        abortRef.current = null;
      }
    },
    [playAnswer],
  );

  const stopRecording = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      recorderRef.current.stop();
    }
  }, []);

  const startRecording = useCallback(async () => {
    setError(null);
    setAnswer(null);

    // This must happen synchronously inside the tap, before any await. The
    // gesture is what authorises playback, and awaiting the microphone first
    // would spend it.
    if (!unlockedRef.current && audioElementRef.current) {
      audioElementRef.current.src = SILENT_MP3;
      audioElementRef.current.play().then(
        () => { unlockedRef.current = true; },
        () => {},
      );
    }

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch {
      setError({
        kind: 'permission',
        message: 'Microphone access was refused.',
      });
      return;
    }

    streamRef.current = stream;
    runMeter(stream);

    const mimeType = pickMimeType();
    let recorder;
    try {
      recorder = mimeType
        ? new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 32000 })
        : new MediaRecorder(stream);
    } catch {
      // Some builds reject the options object outright; the default settings
      // still produce something usable.
      recorder = new MediaRecorder(stream);
    }

    recorderRef.current = recorder;
    chunksRef.current = [];

    recorder.ondataavailable = (event) => {
      if (event.data?.size) chunksRef.current.push(event.data);
    };

    recorder.onstop = () => {
      if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;

      const type = recorder.mimeType || mimeType || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type });
      chunksRef.current = [];

      releaseMicrophone();

      // Under about this size there is no speech in the recording, only room
      // tone. Uploading it would spend a question on nothing.
      if (blob.size < 1200) {
        setStatus(STATUS.idle);
        setError({ kind: 'tooShort', message: 'That was too short.' });
        return;
      }

      send({ audio: blob });
    };

    recorder.start();
    setStatus(STATUS.listening);

    stopTimerRef.current = setTimeout(stopRecording, MAX_RECORDING_MS);
  }, [releaseMicrophone, runMeter, send, stopRecording]);

  const stopSpeaking = useCallback(() => {
    const element = audioElementRef.current;
    if (element) {
      element.pause();
      element.currentTime = 0;
    }
    setStatus(STATUS.idle);
  }, []);

  /** One control for everything: whatever is happening, the tap does the
   * sensible next thing. */
  const toggle = useCallback(() => {
    if (status === STATUS.listening) {
      stopRecording();
      return;
    }
    if (status === STATUS.speaking) {
      stopSpeaking();
      return;
    }
    if (status === STATUS.thinking) {
      abortRef.current?.abort();
      setStatus(STATUS.idle);
      return;
    }
    startRecording();
  }, [status, startRecording, stopRecording, stopSpeaking]);

  const askText = useCallback(
    (text) => {
      if (!text.trim()) return;
      if (!unlockedRef.current && audioElementRef.current) {
        audioElementRef.current.src = SILENT_MP3;
        audioElementRef.current.play().then(
          () => { unlockedRef.current = true; },
          () => {},
        );
      }
      send({ text: text.trim() });
    },
    [send],
  );

  // The answer finishing is what returns the button to idle.
  useEffect(() => {
    const element = audioElementRef.current;
    if (!element) return undefined;
    const onEnded = () => setStatus((current) =>
      current === STATUS.speaking ? STATUS.idle : current,
    );
    element.addEventListener('ended', onEnded);
    return () => element.removeEventListener('ended', onEnded);
  }, []);

  return {
    status,
    level,
    error,
    answer,
    toggle,
    askText,
    supported: micSupported(),
  };
}
